import dayjs, { isDayjs } from 'dayjs'
import { isNotNullish, isNullish } from 'effect/Predicate'
import { z } from 'zod'

import { isPresentValue } from '../../functions/util/isPresentValue.ts'
import type { CoreDslParameter } from '../compile.ts'
import type { FunctionNames } from '../syntax/expression_ast.ts'

// eslint-disable-next-line complexity,sonarjs/cognitive-complexity
export function callFunction(name: FunctionNames, arguments_: unknown[], variable: CoreDslParameter): unknown {
  switch (name) {
    case 'add': {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: ランタイムエラーで良いのか？また効率化できないか？
      const a = z.number().parse(arguments_[0])
      const b = z.number().parse(arguments_[1])
      return a + b
    }
    case 'present': {
      return isPresentValue(arguments_[0])
    }
    case 'blank': {
      return !isPresentValue(arguments_[0])
    }
    case 'equal': {
      const a = arguments_[0]
      const b = arguments_[1]

      if (typeof a === 'string' && typeof b === 'string') {
        const shouldConsiderCase = arguments_[2]
        // 多言語対応の規模が大きくなってきたら、localeCompareの使用を検討する
        return shouldConsiderCase !== false && isNotNullish(shouldConsiderCase)
          ? a === b
          : a.toLowerCase() === b.toLowerCase()
      }
      return a === b
    }
    case 'in': {
      const a = arguments_[0]
      const b = arguments_[1]
      if (!Array.isArray(b)) {
        return false
      }
      if (Array.isArray(a)) {
        return a.every((aa) => b.includes(aa))
      }
      return b.includes(a)
    }
    case 'include': {
      const a = arguments_[0]
      const b = arguments_[1]
      if (Array.isArray(a) && Array.isArray(b)) {
        return b.every((bb) => a.includes(bb))
      }
      if (Array.isArray(a)) {
        return a.includes(b)
      }
      if (typeof a === 'string' && typeof b === 'string') {
        const shouldConsiderCase = arguments_[2]
        // 多言語対応の規模が大きくなってきたら、localeCompareの使用を検討する
        return shouldConsiderCase !== false && isNotNullish(shouldConsiderCase)
          ? a.includes(b)
          : a.toLowerCase().includes(b.toLowerCase())
      }
      // eslint-disable-next-line sonarjs/todo-tag
      return false // TODO: エラーにすべきか
    }
    case 'startsWith': {
      const a = arguments_[0]
      const b = arguments_[1]

      if (typeof a === 'string' && typeof b === 'string') {
        const shouldConsiderCase = arguments_[2]
        // 多言語対応の規模が大きくなってきたら、localeCompareの使用を検討する
        return shouldConsiderCase !== false && isNotNullish(shouldConsiderCase)
          ? a.startsWith(b)
          : a.toLowerCase().startsWith(b.toLowerCase())
      }
      // eslint-disable-next-line sonarjs/todo-tag
      return false // TODO: エラーにすべきか
    }
    case 'overlap': {
      const a = arguments_[0]
      const b = arguments_[1]
      if (Array.isArray(a) && Array.isArray(b)) {
        return a.some((aa) => b.includes(aa))
      }
      // eslint-disable-next-line sonarjs/todo-tag
      return false // TODO: エラーにすべきか
    }
    case 'rank': {
      const a = arguments_[0]
      const b = arguments_[1]
      if (!Array.isArray(b)) {
        return false
      }
      return b.indexOf(a)
    }
    case 'never': {
      return false
    }
    case 'not': {
      const a = arguments_[0]
      return a === false || isNullish(a)
    }
    case 'and': {
      return arguments_.every((x) => x !== false && isNotNullish(x))
    }
    case 'or': {
      return arguments_.some((x) => x !== false && isNotNullish(x))
    }
    case 'date': {
      const x = arguments_[0]
      // undefined, null, 空文字列のときのdate関数の挙動、仕様が悩ましいが、この挙動が使いやすい
      // 「入力なし」の式が date(x) != null になるので、xがnull, undefined, 空文字列のときにnullにしたい
      if (x === null) {
        return null
      }
      if (typeof x !== 'string') {
        return null
      }
      if (x === '') {
        return null
      }
      return dayjs(x).startOf('day')
    }
    case 'fiscal_year': {
      const x = arguments_[0]
      if (!isDayjs(x)) {
        return
      }
      const shape = { setting: z.object({ accountClosingMonth: z.number(), monthBeginningDay: z.number() }) }
      const parsedVariable = z.object(shape).safeParse(variable)
      if (!parsedVariable.success) {
        return
      }
      const thisMonth = x.month() + 1 // month()の結果は0〜11
      const { setting } = parsedVariable.data
      const offset = ((setting.accountClosingMonth - thisMonth + 12) % 12) - 11
      return x.startOf('month').add(offset, 'month')
    }
    case 'year': {
      const x = arguments_[0]
      if (!isDayjs(x)) {
        return
      }
      return x.startOf('year')
    }
    case 'month': {
      const x = arguments_[0]
      if (!isDayjs(x)) {
        return
      }
      return x.startOf('month')
    }
    case 'week': {
      const x = arguments_[0]
      if (!isDayjs(x)) {
        return
      }
      // 週の開始曜日を月曜日に調整する
      const baseDate = x.day() === 0 ? x.subtract(1, 'day') : x
      return baseDate.startOf('week').add(1, 'day')
    }
    case 'today': {
      return dayjs().startOf('day')
    }
    case 'dateAdd': {
      const [date, diff, dateType] = arguments_
      if (date === undefined || diff === undefined || dateType === undefined) {
        return
      }
      if (!isDayjs(date) || typeof diff !== 'number' || typeof dateType !== 'string') {
        return
      }
      if (dateType === 'fiscal_year') {
        return date.add(diff, 'year')
      }
      const parsedDateType = z.enum(['day', 'week', 'month', 'year']).safeParse(dateType)
      if (!parsedDateType.success) {
        return
      }
      return date.add(diff, parsedDateType.data)
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
      const x: never = name
      return null
    }
  }
}
